import { createContext, useContext, useEffect, useState } from 'react';
import { isNativeApp } from '../utils/platform.js';

const KEY = 'rk-theme';
const ThemeContext = createContext({
  theme: 'system',
  resolved: 'light',
  setTheme: () => {},
});

function systemDark() {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolve(theme) {
  if (theme === 'dark') return 'dark';
  if (theme === 'light') return 'light';
  return systemDark() ? 'dark' : 'light';
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    try {
      return localStorage.getItem(KEY) || 'system';
    } catch {
      return 'system';
    }
  });
  const [resolved, setResolved] = useState(() => resolve(theme));

  const setTheme = (next) => {
    setThemeState(next);
    try {
      localStorage.setItem(KEY, next);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    const apply = () => {
      const r = resolve(theme);
      setResolved(r);
      if (!isNativeApp()) {
        document.documentElement.removeAttribute('data-theme');
        return;
      }
      document.documentElement.setAttribute('data-theme', r);
      import('@capacitor/status-bar')
        .then(({ StatusBar, Style }) =>
          StatusBar.setStyle({ style: r === 'dark' ? Style.Dark : Style.Light }),
        )
        .catch(() => {});
    };
    apply();
    if (theme !== 'system') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => apply();
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, resolved, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
